import TableSearch from 'components/Table/TableSearch';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ConfirmationModal from '@enerex/unified-ui-react/ConfirmationModal';
import LoadingButton from '@enerex/unified-ui-react/LoadingButton';
import Table from '@enerex/unified-ui-react/Table';

import PageTitle from 'components/PageTitle/index';
import Text from 'components/Text';
import AddRateClassAssociationModal from './AddRateClassAssociationModal';
import EditMasterRateClassModal from './EditMasterRateClassModal';
import { createMasterRateClassColumns } from './masterRateClassColumns';
import { createMasterRateClass, createSupplierRateClass, createUtilityRateClass, deleteMasterRateClass, getMasterRateClasses } from './rateClassApi';

const MasterRateClassListPage = () => {
  const [masterRateClasses, setMasterRateClasses] = useState([]);
  const [selectedRateClass, setSelectedRateClass] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalMode, setEditModalMode] = useState('edit');
  const [addModalContext, setAddModalContext] = useState({ type: null, utilityRows: [] });
  const [deleteModal, setDeleteModal] = useState({ show: false, item: null });
  const [associationRefreshKey, setAssociationRefreshKey] = useState(0);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const canAddRateClass = false; 

  const rows = masterRateClasses;
  const openEditModal = useCallback((rateClass) => {
    setSelectedRateClass(rateClass);
    setEditModalMode('edit');
    setShowEditModal(true);
  }, []);
  const openDeleteModal = useCallback((rateClass) => {
    setDeleteModal({ show: true, item: rateClass });
  }, []);
  const columns = useMemo(() => createMasterRateClassColumns(openEditModal, openDeleteModal), [openEditModal, openDeleteModal]);

  const loadMasterRateClasses = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      setMasterRateClasses(await getMasterRateClasses(searchTerm));
    } catch (error) {
      setMessage({ variant: 'danger', text: error.message || 'Failed to load master rate classes.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMasterRateClasses();
  }, [loadMasterRateClasses]);

  useEffect(() => {
    if (!message) return undefined;

    const timeout = window.setTimeout(() => {
      setMessage(null);
    }, 5000);

    return () => window.clearTimeout(timeout);
  }, [message]);

  const refreshMasterRateClasses = () => {
    loadMasterRateClasses(search).catch((error) => {
      setMessage({ variant: 'danger', text: error.message || 'Failed to load master rate classes.' });
    });
  };

  const handleSearch = (value) => {
    const nextSearch = value;
    setSearch(nextSearch);
    loadMasterRateClasses(nextSearch);
  };

  const handleSaveMaster = async () => {
    setSelectedRateClass(null);
    setMessage({ variant: 'success', text: 'Master Rate Class saved successfully.' });
    refreshMasterRateClasses();
  };

  const handleCreateMaster = async (values) => {
    const duplicate = masterRateClasses.some((row) => (
      String(row.rateClassName || '').trim().toLowerCase() === values.rateClassName.trim().toLowerCase()
      && String(row.rateCode || '').trim().toLowerCase() === String(values.rateCode || '').trim().toLowerCase()
    ));

    if (duplicate) {
      throw new Error('Master rate class already exists.');
    }

    const createdRateClass = await createMasterRateClass(values);
    setSelectedRateClass(null);
    setMessage({ variant: 'success', text: 'Master Rate Class added successfully.' });
    refreshMasterRateClasses();
    return createdRateClass;
  };

  const handleAddAssociation = async (values) => {
    if (!selectedRateClass) return;

    try {
      if (addModalContext.type === 'utility') {
        await createUtilityRateClass({
          rateClassId: selectedRateClass.rateClassId,
          commodityId: values.commodityId,
          commodity: values.commodity,
          utilityId: values.companyId,
          utilityName: values.companyName,
          rateClassName: values.rateClassName,
        });
        setMessage({ variant: 'success', text: 'Utility Rate Class added successfully.' });
      } else {
        await createSupplierRateClass({
          rateClassId: selectedRateClass.rateClassId,
          commodityId: values.commodityId,
          commodity: values.commodity,
          supplierId: values.companyId,
          supplierName: values.companyName,
          utilityId: values.utilityId,
          utilityName: values.utilityName,
          utilityRateClassId: values.utilityRateClassId,
          rateClassName: values.rateClassName,
        });
        setMessage({ variant: 'success', text: 'Supplier Rate Class added successfully.' });
      }
      setAssociationRefreshKey((value) => value + 1);
      setAddModalContext({ type: null, utilityRows: [] });
    } catch (error) {
      setMessage({ variant: 'danger', text: error.message || 'Failed to add rate class.' });
      setAddModalContext({ type: null, utilityRows: [] });
    }
  };

  const handleDeleteMaster = async () => {
    const rateClassId = deleteModal.item?.rateClassId;

    if (!rateClassId) {
      setMessage({ variant: 'danger', text: 'Unable to delete master rate class: missing record id.' });
      return;
    }

    setDeleting(true);
    try {
      await deleteMasterRateClass(rateClassId);
      setMessage({ variant: 'success', text: 'Master Rate Class deleted successfully.' });
      setDeleteModal({ show: false, item: null });
      await loadMasterRateClasses();
    } catch (error) {
      setMessage({ variant: 'danger', text: error.message || 'Failed to delete master rate class.' });
      setDeleteModal({ show: false, item: null });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between flex-wrap company-page-header">
        <div className="d-flex align-items-center flex-wrap company-page-title-row">
          {/* <PageTitle title="Rate Classes" />
          <Text variant="subtitle" className="ms-md-2 company-page-total-records" style={{ marginTop: '16px' }}>
            {rows.length} records
          </Text> */}
        </div>
      </div>

      {message ? (
        <div className={`alert alert-${message.variant} alert-dismissible`} role="alert">
          {message.text}
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setMessage(null)} />
        </div>
      ) : null}

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body py-2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div style={{ width: 320 }}>
          <TableSearch appliedSearch={search} value={searchInput} onChange={setSearchInput} onSearch={handleSearch}
            placeholder="Search rate classes..." />
        </div>
                {canAddRateClass ? (
  <LoadingButton size="sm" variant="primary" onClick={() => {
    setSelectedRateClass(null);
    setEditModalMode('add');
    setShowEditModal(true);
  }}>
    <i className="uil uil-plus me-1" />Add Rate Class
  </LoadingButton>
) : null}
              </div>
              <Table
                id="master-rate-classes"
                data={rows}
                columns={columns}
                loading={loading}
                total={rows.length}
                enableSearch={false}
                enablePagination={rows.length > 0}
                enableSorting={true}
                enableFilters={false}
                enableExport={false}
                enableHideColumns={false}
                noDataMessage="No rate classes found"
              />
            </div>
          </div>
        </div>
      </div>

      <EditMasterRateClassModal
        show={showEditModal}
        rateClass={selectedRateClass}
        mode={editModalMode}
        refreshKey={associationRefreshKey}
        onSave={handleSaveMaster}
        onCreate={handleCreateMaster}
        onMessage={setMessage}
        onAddAssociation={(type, utilityRows = []) => setAddModalContext({ type, utilityRows })}
        onHide={() => {
          setShowEditModal(false);
          setAddModalContext({ type: null, utilityRows: [] });
          setSelectedRateClass(null);
        }}
      />

      <AddRateClassAssociationModal
        show={Boolean(addModalContext.type)}
        type={addModalContext.type}
        utilityRows={addModalContext.utilityRows}
        onHide={() => setAddModalContext({ type: null, utilityRows: [] })}
        onSave={handleAddAssociation}
      />

      <ConfirmationModal
        centered
        show={deleteModal.show}
        onHide={() => {
          if (!deleting) setDeleteModal({ show: false, item: null });
        }}
        title="Delete Master Rate Class"
        confirmButtonLabel={deleting ? 'Deleting...' : 'Delete'}
        confirmButtonVariant="danger"
        isLoading={deleting}
        onConfirm={handleDeleteMaster}
      >
        Are you sure you want to delete <strong>{deleteModal.item?.rateClassName}</strong>?
      </ConfirmationModal>
    </>
  );
};

export default MasterRateClassListPage;
